import '@styles/index.css';
import { clearLocalStoragePreserveLanguage } from '@utils/utils';
import React, { useState, useRef, useEffect } from 'react';
import { confirmSignUp, resendSignUpCode, signIn } from 'aws-amplify/auth';
import {
    setEmail,
    setDeviceUniqueID,
    setMobileNumber,
    setPassword,
    setVerified,
    // setStepPage
} from '@store/reducers/auth/signupSlice';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
    HOME_PATH,
    SIGNUP_PATH,
    LOGIN_PATH,
    OTP_PATH,
} from '@constants/routes';
import { EMAIL, HOSPITAL, INDIVIDUAL, WAITING } from '@constants/constants';
import {
    loginSuccess,
    setLoggedInUserRole,
    setLoggedInUserStatus,
} from '@store/reducers/auth/loginSlice';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import AuthLayout from '@components/Layout/auth/AuthLayout';
import { OTP_MOBILE_SRC, OTP_EMAIL_SRC } from '@constants/images';
import awsConfig from '../../../amplifyconfiguration.json';
import { DeleteItemCommand } from '@aws-sdk/client-dynamodb';
import { DeleteObjectCommand } from '@aws-sdk/client-s3';
import { AdminDeleteUserCommand } from '@aws-sdk/client-cognito-identity-provider';
import ErrorMessage from '@components/Error/ErrorMessage';
import { client, dbClient, s3Client } from '@utils/awsClients';
import { getUserFromCognito } from '@utils/login';
import {
    getDataUsingTenantId,
    getDataUsingUserId,
    sendNotification,
} from '@utils/sharedUtils';
import { USER_INFO_TABLE } from '@constants/dynamoDBTableName';
import { logout } from '@store/reducers/auth/authReducer';
import { API_KEY } from '@constants/AppConfig';
import { CHANGE_EMAIL_MOBILE_API } from '@constants/auth/Api';

const Verification = () => {
    const [confirmationCode, setConfirmationCode] = useState(Array(6).fill(''));
    const [isClickable, setIsClickable] = useState(false);
    const [countdown, setCountdown] = useState(30);
    const inputRefs = useRef<HTMLInputElement[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const dispatch = useAppDispatch();
    const email = useAppSelector((state) => state.signup.email);
    const showLink = useAppSelector((state) => state.signup.showLink);
    const mobileNumber = useAppSelector((state) => state.signup.mobileNumber);
    const password = useAppSelector((state) => state.signup.password);
    const deviceUniqueID = useAppSelector(
        (state) => state.signup.deviceUniqueID
    );
    const userRole = useAppSelector((state) => state.signup.userRole);
    const otpType = useAppSelector((state) => state.signup.otpType);
    const userUuid = useAppSelector((state) => state.signup.user_uuid);
    const tenantUuid = useAppSelector((state) => state.signup.tenant_uuid);
    const hospitalUuid = useAppSelector((state) => state.signup.hospital_uuid);
    const hospitalInfoUuid = useAppSelector(
        (state) => state.signup.hospital_info_uuid
    );
    const hospitalTenantUuid = useAppSelector(
        (state) => state.signup.hospital_tenant_uuid
    );
    const logo_name = useAppSelector((state) => state.signup.logo_name);
    const userInfoUuid = useAppSelector((state) => state.signup.user_info_uuid);

    const { t } = useTranslation();
    const navigate = useNavigate();

    async function handleBeforeUnload(event: any) {
        const message = 'Are you sure you want to leave?';

        event.preventDefault();

        event.returnValue = message;

        return message;
    }

    useEffect(() => {
        // window.addEventListener('beforeunload', handleBeforeUnload);

        if (email == '') {
            navigate(LOGIN_PATH);
        } else {
            startCountdown();
        }

        // return () => {
        //     window.removeEventListener('beforeunload', handleBeforeUnload);
        // };
    }, [email]);

    const obscureEmail = (email: string): string => {
        const [username, domain] = email.split('@');

        if (email != '') {
            const obscuredUsername = `${username.charAt(0)}${'*'.repeat(username.length - 2)}${username.charAt(username.length - 1)}`;
            const obscuredDomain = `${domain.charAt(0)}${'*'.repeat(domain.length - 2)}${domain.charAt(domain.length - 1)}`;

            return `${obscuredUsername}@${obscuredDomain}`;
        } else {
            return 's****@***m';
        }
    };

    let lastThreeDigits;

    if (mobileNumber != '') {
        lastThreeDigits = mobileNumber.slice(-3);
    } else {
        lastThreeDigits = '*******';
    }

    const handleCodeChange = (
        index: number,
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const newValue = event.target.value;

        if (newValue.length <= 1) {
            // Ensure only one character is entered
            const newValues = [...confirmationCode];

            newValues[index] = newValue;
            setConfirmationCode(newValues);

            // Move to the next input field if available
            if (newValue && index < inputRefs.current.length - 1) {
                inputRefs.current[index + 1].focus();
            }
        }

        setError(null);
    };

    const handleKeyDown = (
        index: number,
        event: React.KeyboardEvent<HTMLInputElement>
    ) => {
        const key = event.key;
        if (
            (key >= '0' && key <= '9') ||
            key === 'Backspace' ||
            key === 'Delete' ||
            key === 'ArrowLeft' ||
            key === 'ArrowRight' ||
            (event.ctrlKey && key.toLowerCase() === 'v') ||
            (event.ctrlKey && key.toLowerCase() === 'a')
        ) {
            // Special handling for backspace
            if (key === 'Backspace') {
                // If the field is empty and it's not the first field, move focus to the previous field
                if (event.currentTarget.value === '' && index > 0) {
                    inputRefs.current[index - 1]?.focus();
                }
            }
            return;
        }

        // Prevent all other keys
        event.preventDefault();
    };

    const handlePaste = (
        index: number,
        event: React.ClipboardEvent<HTMLInputElement>
    ) => {
        const pastedData = event.clipboardData.getData('text').slice(0, 6); // Limit to 6 characters
        const newValues = [...confirmationCode];

        for (
            let i = 0;
            i < pastedData.length && index + i < newValues.length;
            i++
        ) {
            newValues[index + i] = pastedData[i];
        }

        setConfirmationCode(newValues);

        // Focus on the next empty input field
        const nextIndex =
            index + pastedData.length < newValues.length
                ? index + pastedData.length
                : newValues.length - 1;

        inputRefs.current[nextIndex].focus();
    };

    const handleConfirmCode = async (event: React.SyntheticEvent) => {
        event.preventDefault();
        setIsSubmitting(true);
        setError(null);
        try {
            const { isSignUpComplete, userId, nextStep } = await confirmSignUp({
            username: email,
            confirmationCode: confirmationCode.join(''),
            });
            if (showLink) {
                const notificationData = {
                    type: 'email',
                    users: [email] as string[], // Sending recipient email/phone/deviceToken based on type
                    fromAddress: 'support@dripset.net',
                    toAddress: [email],
                    schedule: null, // Include schedule if applicable
                };

                // Send the data to the sharedUtils.ts function
                await sendNotification(notificationData);
                // await handleSignIn();
                dispatch(setVerified(true));
                navigate(LOGIN_PATH);
            } else {
                dispatch(setVerified(true));
                navigate(LOGIN_PATH);
            }
        } catch (error: any) {
            setIsSubmitting(false);
            let errorMessage = 'Internal Server Error';

            switch (error.name) {
                case 'UserNotFoundException':
                    errorMessage = 'Invalid credentials.';
                    break;
                case 'CodeMismatchException':
                case 'ExpiredCodeException':
                    errorMessage = 'Invalid or expired confirmation code.';
                    break;
                case 'TooManyRequestsException':
                    errorMessage = 'Too many requests. Please try again later.';
                    break;
                case 'InvalidParameterException':
                    errorMessage = 'Invalid parameters provided.';
                    break;
                case 'NotAuthorizedException':
                    errorMessage =
                        'You are not authorized to perform this action.';
                    break;
                default:
                    errorMessage = error.message || 'An unknown error occurred';
            }
            setError(errorMessage);
        }
    };

    async function handleSignIn() {
        try {
            // await signIn({
            //     username: email,
            //     password,
            // });
            // dispatch(loginSuccess());
            // window.location.href = HOME_PATH;
            const user = getUserFromCognito(email);

            if (user && (await user).UserAttributes) {
                const attributes = (await user).UserAttributes;
                const tenantId = attributes
                    ? attributes.find(
                          (attr: any) => attr.Name === 'custom:tenant_id'
                      )?.Value || ''
                    : '';
                const currentUserId = attributes
                    ? attributes.find((attr: any) => attr.Name === 'sub')
                          ?.Value || ''
                    : '';
                const role = attributes
                    ? attributes.find(
                          (attr: any) => attr.Name === 'custom:role'
                      )?.Value || ''
                    : '';

                if (role.toLowerCase() == INDIVIDUAL.toLowerCase()) {
                    await signIn({
                        username: email,
                        password,
                    });
                    dispatch(loginSuccess());
                    window.location.href = HOME_PATH;
                } else {
                    clearLocalStoragePreserveLanguage();
                    dispatch(logout());
                    dispatch(setLoggedInUserRole(role));
                    dispatch(setLoggedInUserStatus(WAITING));
                    navigate(HOME_PATH);
                }

                // const userInfo = await getDataUsingUserId(
                //     USER_INFO_TABLE,
                //     currentUserId
                // );

                // if (userInfo && userInfo.Items && userInfo.Items?.length != 0) {
                //     const res = userInfo.Items[0];
                //     const status = res.status?.S;
                //     if (
                //         status &&
                //         status.toLowerCase() != 'approved' &&
                //         status.toLowerCase() != 'verified'
                //     ) {
                //         localStorage.clear();
                //         dispatch(logout());
                //         dispatch(setLoggedInUserRole(role));
                //         dispatch(setLoggedInUserStatus(status));
                //         navigate(HOME_PATH);
                //         return;
                //     }
                // }
            }
        } catch (error: any) {
            let errorMessage = 'Internal Server Error';

            switch (error.name) {
                case 'UserNotFoundException':
                case 'NotAuthorizedException':
                    errorMessage = 'Invalid credential.';
                    break;
                case 'TooManyRequestsException':
                    errorMessage = 'Too many requests. Please try again later.';
                    break;
                case 'InvalidParameterException':
                    errorMessage = 'Invalid parameters provided.';
                    break;
                default:
                    errorMessage = error.message || 'An unknown error occurred';
                    break;
            }

            setError(errorMessage);
        }
    }

    const startCountdown = () => {
        setIsClickable(false);
        let timer = setInterval(() => {
            setCountdown((prevCountdown) => {
                if (prevCountdown === 1) {
                    clearInterval(timer);
                    setIsClickable(true);
                    return 30;
                } else {
                    return prevCountdown - 1;
                }
            });
        }, 1000);
        setTimeout(() => {
            clearInterval(timer);
            setIsClickable(true);
            setCountdown(30);
        }, 30000);
    };

    const handleResendCode = async (event: React.SyntheticEvent) => {
        event.preventDefault();
        if (isClickable) {
            startCountdown();
            setIsClickable(false);
            try {
                await resendSignUpCode({
                    username: email,
                });
            } catch (error: any) {
                let errorMessage = 'Internal Server Error';

                switch (error.name) {
                    case 'UserNotFoundException':
                        errorMessage = 'Invalid credentials.';
                        break;
                    case 'TooManyRequestsException':
                        errorMessage =
                            'Too many requests. Please try again later.';
                        break;
                    case 'InvalidParameterException':
                        errorMessage = 'Invalid parameters provided.';
                        break;
                    default:
                        errorMessage =
                            error.message || 'An unknown error occurred';
                        break;
                }

                setError(errorMessage);
            }
        }
    };

    const changeCredentials = async () => {
        try {
            const userName = otpType === EMAIL ? email : mobileNumber;
            const otpTypeValue = otpType === EMAIL ? 'email' : 'mobile';

            const response = await fetch(CHANGE_EMAIL_MOBILE_API, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': API_KEY,
                },
                body: JSON.stringify({
                    userName: userName,
                    otpType: otpTypeValue,
                }),
            });

            if (!response.ok) {
                throw new Error(`API request failed with status ${response.status}`);
            }

            const result = await response.json();

            dispatch(setDeviceUniqueID(deviceUniqueID));
            dispatch(setEmail(email));
            dispatch(setPassword(password));
            dispatch(setMobileNumber(mobileNumber));
        } catch (error: any) {
            let errorMessage = 'Unexpected error';

            switch (error.name) {
                case 'ResourceNotFoundException':
                    errorMessage = 'Table or item not found.';
                    break;
                case 'ConditionalCheckFailedException':
                    errorMessage = 'Conditional check failed.';
                    break;
                case 'ProvisionedThroughputExceededException':
                    errorMessage = 'Provisioned throughput exceeded.';
                    break;
                case 'ItemCollectionSizeLimitExceededException':
                    errorMessage = 'Item collection size limit exceeded.';
                    break;
                case 'InternalServerError':
                    errorMessage = 'Internal server error.';
                    break;
                case 'ValidationException':
                    errorMessage = `Validation exception - ${error.message}`;
                    break;
                case 'NoSuchBucket':
                    errorMessage = 'Bucket does not exist.';
                    break;
                case 'NoSuchKey':
                    errorMessage = 'Object does not exist.';
                    break;
                case 'AccessDenied':
                    errorMessage = 'Access denied.';
                    break;
                case 'InvalidBucketName':
                    errorMessage = 'Invalid bucket name.';
                    break;
                case 'UserNotFoundException':
                    errorMessage = 'User not found.';
                    break;
                case 'NotAuthorizedException':
                    errorMessage =
                        'You are not authorized to perform this action.';
                    break;
                case 'InvalidParameterException':
                    errorMessage = 'Invalid parameters provided.';
                    break;
                default:
                    errorMessage = error.message || 'An error occurred while changing email/mobile';
            }

            setError(errorMessage);
        }
    };

    const handleBackClick = () => {
        if (showLink) {
            navigate(OTP_PATH);
        } else {
            navigate(LOGIN_PATH);
        }
    };

    const deleteCommand = async (
        tableName: string,
        key: { [key: string]: any }
    ) => {
        const params = {
            TableName: tableName,
            Key: key,
        };

        try {
            const command = new DeleteItemCommand(params);
            const response = await dbClient.send(command);
        } catch (error: any) {
            throw error;
        }
    };

    const deleteObject = async () => {
        const params = {
            Bucket: 'dripsetapplicationstaging',
            Key: logo_name,
        };

        try {
            const command = new DeleteObjectCommand(params);
            const response = await s3Client.send(command);
        } catch (error: any) {
            throw error;
        }
    };

    const deleteUser = async () => {
        const params = {
            UserPoolId: awsConfig.aws_user_pools_id,
            Username: email,
        };

        try {
            const command = new AdminDeleteUserCommand(params);
            const response = await client.send(command);
        } catch (error: any) {
            throw error;
        }
    };

    return (
        <>
            <AuthLayout onBackClick={handleBackClick}>
                <div>
                    <div className="mx-auto flex justify-center my-5 w-56 2xl:h-32">
                        {otpType === EMAIL ? (
                            <>
                                <img
                                    src={OTP_EMAIL_SRC}
                                    alt="OTP_EMAIL"
                                    className="object-contain"
                                />
                            </>
                        ) : (
                            <>
                                <img
                                    src={OTP_MOBILE_SRC}
                                    alt="OTP_MOBILE"
                                    className="object-contain"
                                />
                            </>
                        )}
                    </div>

                    <div className="text-center mb-3 mx-auto w-1/3">
                        <h4 className="text-2xl font-medium text-black mb-2">
                            {t('auth.verify_your_account')}
                        </h4>

                        <p className="text-netural_black font-normal text-wrap">
                            {otpType === EMAIL ? (
                                <>
                                    {t('auth.your_code_text')}
                                    <br />
                                    {t('auth.your_code_text_email')}{' '}
                                    {obscureEmail(email)}
                                </>
                            ) : (
                                <>
                                    {t('auth.your_code_text_mobile')} <br />
                                    {t(
                                        'auth.your_code_text_mobile_number'
                                    )}{' '}
                                    xxxxxxx {lastThreeDigits}
                                </>
                            )}
                        </p>
                        {showLink && (
                            <p className="!text-primary font-semibold my-2">
                                {otpType === EMAIL ? (
                                    <>
                                        {t('auth.wrong_email')}
                                        <Link
                                            to={SIGNUP_PATH}
                                            onClick={changeCredentials}
                                            className="!text-primary font-semibold hover:underline underline-offset-4"
                                        >
                                            {t('auth.change_email')}
                                        </Link>
                                    </>
                                ) : (
                                    <>
                                        {t('auth.wrong_mobile_number')}
                                        <Link
                                            to={SIGNUP_PATH}
                                            onClick={changeCredentials}
                                            className="!text-primary font-semibold hover:underline underline-offset-4"
                                        >
                                            {t('auth.change_mobile')}
                                        </Link>
                                    </>
                                )}
                            </p>
                        )}

                        {error && <ErrorMessage message={error} />}
                    </div>
                    <div>
                        <div className="flex flex-col items-center justify-center">
                            <div className="card w-2/6 shadow-none border-none rounded-2xl">
                                <div className="card-body p-2">
                                    <form onSubmit={handleConfirmCode}>
                                        <div className="">
                                            <div className="auth-input-wrapper d-flex align-items-center justify-content-center numeral-mask-wrapper">
                                                {confirmationCode.map(
                                                    (value, index) => (
                                                        <input
                                                            key={index}
                                                            type="text"
                                                            aria-label="VerificationCode"
                                                            maxLength={1}
                                                            value={
                                                                confirmationCode[
                                                                    index
                                                                ] || ''
                                                            }
                                                            onChange={(event) =>
                                                                handleCodeChange(
                                                                    index,
                                                                    event
                                                                )
                                                            }
                                                            onKeyDown={(
                                                                event
                                                            ) =>
                                                                handleKeyDown(
                                                                    index,
                                                                    event
                                                                )
                                                            }
                                                            onPaste={(event) =>
                                                                handlePaste(
                                                                    index,
                                                                    event
                                                                )
                                                            }
                                                            ref={(input) => {
                                                                if (input) {
                                                                    inputRefs.current[
                                                                        index
                                                                    ] = input;
                                                                }
                                                            }}
                                                            required
                                                            className="form-control auth-input h-px-50 text-center numeral-mask 2xl:mr-4 xl:mr-4 lg:mr-2 bg-otp my-2 lg:w-12 lx:w-12 2xl:w-1/12"
                                                        />
                                                    )
                                                )}
                                            </div>
                                        </div>
                                        <div className="absolute -bottom-28 left-1/2 transform -translate-x-1/2 2xl:w-3/4 lg:w-full">
                                            {isClickable ? (
                                                <div
                                                    className="!text-primary text-sm px-5 py-2.5 text-center cursor-pointer"
                                                    onClick={handleResendCode}
                                                >
                                                    {t('auth.resend')}
                                                </div>
                                            ) : (
                                                <div className="!text-primary text-sm px-5 py-2.5 text-center">
                                                    {t('auth.resend')} (
                                                    {countdown}
                                                    s)
                                                </div>
                                            )}

                                            <button
                                                type="submit"
                                                disabled={isSubmitting}
                                                className="w-full text-white !bg-primary mt-3 mb-3 !hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {isSubmitting ? t('auth.submit') : t('auth.submit')}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </AuthLayout>
        </>
    );
};

export default Verification;
